import { locationApi } from '@/api/account-management/locationApi';
import { createLocation, type Location } from '@/models/account-management/Location';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import Dialog from '../common/dialog/Dialog';
import FormTextField from '../common/FormTextField';
import { useToast } from '../common/toast/ToastProvider';

interface NewLocationDialogProps {
    open: boolean;
    onClose: () => void;
    type: number;
    organizationId: string;
    brandId: string;
}

interface NewLocationForm {
    name: string;
}

// Minimal create step: a client/admin only needs to name the location. That persists a draft record
// (everything else defaults empty on the backend), then we navigate to LocationDetails to fill in the
// rest. Completeness for enablement is driven separately by the readiness checks.
const NewLocationDialog = ({ open, onClose, type, organizationId, brandId }: NewLocationDialogProps) => {
    const strings = useLocalizedStrings();
    const toast = useToast();
    const navigate = useNavigate();
    const qc = useQueryClient();
    const [saving, setSaving] = useState(false);

    const { register, handleSubmit, reset, formState } = useForm<NewLocationForm>({ defaultValues: { name: '' } });

    const handleClose = () => {
        reset({ name: '' });
        onClose();
    };

    const onSubmit = async (data: NewLocationForm) => {
        setSaving(true);
        try {
            const location: Location = {
                ...createLocation(type, organizationId, brandId),
                name: data.name.trim(),
                // Restaurant-type locations require a Restaurant sub-object on the backend.
                restaurant: type === 0 ? { types: 0 } : undefined,
            };

            const created = (await locationApi.create(location)) as unknown as { id?: string };
            if (!created?.id) throw new Error('Create did not return an id.');

            // The edit route loads by slug, which the server generates from the name.
            const full = await locationApi.get(created.id);

            // Show the new location in the location list without a manual reload.
            await qc.invalidateQueries({ queryKey: ['locations'] });

            reset({ name: '' });
            onClose();
            navigate(`/touchtab/admin/configuration/locations/${full.slug}`);
        } catch {
            toast({ severity: 'error', message: strings.locationSaveError });
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            title={strings.locationNewTitle}
            formId="new-location-form"
            formIsDirty={formState.isDirty}
            saving={saving}
            saveButtonTitle={strings.create}
            cancelButtonTitle={strings.cancel}
            onCancel={handleClose}
        >
            <form id="new-location-form" onSubmit={handleSubmit(onSubmit)}>
                <FormTextField
                    label={strings.name}
                    autoFocus
                    sx={{ width: 360 }}
                    error={!!formState.errors.name}
                    helperText={formState.errors.name?.message || ' '}
                    {...register('name', {
                        required: strings.validationNameRequired,
                        validate: (v) => v.trim().length > 0 || strings.validationNameRequired,
                    })}
                />
            </form>
        </Dialog>
    );
};

export default NewLocationDialog;
